"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import type { SupabaseClient } from "@supabase/supabase-js";
import { Bot, LoaderCircle, RefreshCw, ShieldCheck } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";

type M4Access = {
  allowed?: boolean;
  actor_role?: string;
  profile_key?: string;
  scope_mode?: string;
  max_risk_level?: string;
  capabilities?: Record<string, boolean>;
  approval_policy?: Record<string, string>;
  explicit_admin_grant?: boolean | null;
  expires_at?: string | null;
};

type M4User = {
  email: string;
  user_id?: string | null;
  role: "OWNER_ADMIN" | "ADMIN" | "MEMBER";
  active: boolean;
  profile_key: string;
  m4_enabled: boolean;
  accessible_user_ids?: string[];
  max_risk_level_override?: string | null;
  expires_at?: string | null;
};

export function M4ControlTowerPanel({
  supabase,
  isOwner,
}: {
  supabase: SupabaseClient | null;
  isOwner: boolean;
}) {
  const [selfAccess, setSelfAccess] = useState<M4Access | null>(null);
  const [users, setUsers] = useState<M4User[]>([]);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!supabase) return;
    setBusy(true);
    setNotice(null);

    const { data: account, error: accountError } = await supabase.rpc("runyi_my_account");
    if (accountError) {
      setNotice(accountError.message);
      setBusy(false);
      return;
    }
    setSelfAccess((account?.m4_access ?? null) as M4Access | null);

    if (isOwner) {
      const { data, error } = await supabase.rpc("runyi_owner_m4_access_catalogue");
      if (error) setNotice(error.message);
      else setUsers(Array.isArray(data?.users) ? data.users as M4User[] : []);
    }

    setBusy(false);
  }, [isOwner, supabase]);

  useEffect(() => {
    void load();
  }, [load]);

  const capabilitySummary = useMemo(() => {
    const capabilities = selfAccess?.capabilities ?? {};
    const allowed = Object.entries(capabilities)
      .filter(([, value]) => value === true)
      .map(([key]) => key.replace(/^m4\./, ""));
    return allowed.length ? allowed.join(" · ") : "No M4 capabilities";
  }, [selfAccess]);

  async function setAdminProfile(
    user: M4User,
    profileKey: "MEMBER_DEFAULT" | "ADMIN_READ_ONLY" | "ADMIN_OPERATOR",
    enabled = true,
  ) {
    if (!supabase || user.role !== "ADMIN") return;
    setBusy(true);
    setNotice(null);

    const { data, error } = await supabase.functions.invoke("runyi-control-tower", {
      body: {
        operation: "owner_m4_set_access",
        email: user.email,
        profile_key: profileKey,
        enabled,
        accessible_user_ids: user.accessible_user_ids ?? [],
        capability_overrides: {},
        max_risk_level_override: null,
        expires_at: null,
        reason: enabled
          ? `Owner assigned M4 profile ${profileKey} from Control Tower.`
          : "Owner revoked delegated M4 Admin management.",
      },
    });
    const errorMessage = error?.message ?? (data?.error ? String(data.error) : null);

    setNotice(
      errorMessage
        ? errorMessage
        : enabled
          ? `${user.email} now uses M4 profile ${profileKey}.`
          : `Delegated M4 management revoked for ${user.email}; self-only limited access remains.`,
    );
    if (!errorMessage) await load();
    setBusy(false);
  }

  return (
    <Card className="border-foreground/10 bg-card py-0">
      <CardContent className="space-y-4 p-4 sm:space-y-5 sm:p-7">
        <div className="flex items-start gap-4">
          <div className="grid size-8 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary">
            <Bot className="size-3.5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-semibold">RUNYI M4 AI Control Tower</p>
              <Badge className="bg-foreground/8 text-foreground/70">
                {selfAccess?.profile_key ?? "LOADING"}
              </Badge>
            </div>
            <p className="mt-1 text-sm leading-6 text-foreground/48">
              M4 has the same technical management ceiling as Owner ChatGPT, but effective authority is always restricted by caller role, Owner-granted profile, tenant scope, risk ceiling, approval policy and tenant lifecycle guardrails.
            </p>
          </div>
        </div>

        <div className="rounded-xl border border-foreground/9 bg-[var(--surface-inset)] p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge className="bg-primary/10 text-primary">Scope {selfAccess?.scope_mode ?? "UNKNOWN"}</Badge>
            <Badge className="bg-foreground/8 text-foreground/70">Risk ≤ {selfAccess?.max_risk_level ?? "UNKNOWN"}</Badge>
            {selfAccess?.actor_role === "OWNER_ADMIN" && (
              <Badge className="bg-emerald-400/10 text-emerald-300">Owner-grade M4</Badge>
            )}
          </div>
          <p className="mt-3 text-xs leading-5 text-foreground/48">{capabilitySummary}</p>
        </div>

        {isOwner && (
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-semibold">Owner-controlled M4 delegation</p>
                <p className="mt-1 text-xs leading-5 text-foreground/42">
                  Members always default to self-only limited M4. Admins receive assigned-tenant M4 capability only when you explicitly select a profile here.
                </p>
              </div>
              <Button variant="outline" onClick={() => void load()} disabled={busy} className="h-9 border-foreground/12 bg-transparent">
                {busy ? <LoaderCircle className="animate-spin" /> : <RefreshCw />}Refresh
              </Button>
            </div>

            {users.map((user) => (
              <div key={user.email} className="rounded-xl border border-foreground/8 bg-[var(--surface-inset)] p-3">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{user.email}</p>
                    <p className="mt-1 text-xs text-foreground/42">
                      {user.role} · M4 {user.profile_key} · {user.accessible_user_ids?.length ?? 0} assigned account(s)
                    </p>
                  </div>
                  <Badge className={user.role === "OWNER_ADMIN" ? "bg-emerald-400/10 text-emerald-300" : "bg-foreground/8 text-foreground/70"}>
                    {user.role === "OWNER_ADMIN" ? "FULL" : user.m4_enabled ? "ACTIVE" : "LIMITED"}
                  </Badge>
                </div>

                {user.role === "ADMIN" && (
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button variant="outline" disabled={busy} onClick={() => void setAdminProfile(user, "ADMIN_READ_ONLY", true)} className="h-9 border-foreground/12 bg-transparent px-2.5 text-xs">
                      Assigned read
                    </Button>
                    <Button variant="outline" disabled={busy} onClick={() => void setAdminProfile(user, "ADMIN_OPERATOR", true)} className="h-9 border-foreground/12 bg-transparent px-2.5 text-xs">
                      Assigned operator
                    </Button>
                    <Button variant="outline" disabled={busy} onClick={() => void setAdminProfile(user, "MEMBER_DEFAULT", false)} className="h-9 border-foreground/12 bg-transparent px-2.5 text-xs">
                      Revoke M4 Admin
                    </Button>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <div className="rounded-xl border border-emerald-400/15 bg-emerald-400/[0.045] p-4">
          <div className="flex items-center gap-2 text-emerald-300">
            <ShieldCheck className="size-4" />
            <p className="text-xs font-semibold uppercase tracking-wider">Enterprise guardrail</p>
          </div>
          <p className="mt-2 text-xs leading-5 text-foreground/48">
            The AI never authorizes itself. High-risk changes require Owner approval; forbidden platform invariants such as exposing Vault secrets or modifying OWNER_ADMIN through ordinary management remain blocked.
          </p>
        </div>

        {notice && (
          <div className="rounded-xl border border-amber-300/20 bg-amber-300/8 p-4 text-sm leading-6 text-amber-100">
            {notice}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
